import { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight,
  Eye, X, Cpu, Cloud, HardDrive,
} from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

/**
 * Zabeleženi pozivi ka PFR-u - alat za veštačenje (ADR-037).
 *
 * Kad se povede spor oko računa, ovde se vidi **šta je tačno otišlo PFR-u i šta
 * je vratio**, uključujući neuspehe i prekide veze, kojih pre nije bilo nigde.
 *
 * Provera lanca je razlog zbog kog zapis uopšte vredi kao dokaz, pa je gore, na
 * vrhu - a ne zakopana u meni. Dva nalaza se namerno razlikuju:
 *
 *   „telo je menjano"    → neko je dirao zapis u bazi
 *   „nedostaje zapis"    → poziv se desio, a zapis nije stigao sa uređaja
 */

type CallRow = {
  id: string;
  tenantId: string;
  tenantName: string | null;
  deviceId: string;
  operatorId: string | null;
  provider: 'vpfr' | 'lpfr' | 'mock';
  operation: string;
  requestId: string | null;
  entityId: string | null;
  responseCode: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  occurredAt: string;
  recordedAt: string;
  deviceSeq: number;
  chainHash: string;
  /** Broj koji je PFR vratio i koji stoji na isečku; `null` kad poziv nije prošao. */
  pfrNumber: string | null;
};

type CallDetail = CallRow & {
  url: string;
  httpMethod: string;
  requestBody: unknown;
  responseBody: unknown;
  payloadHash: string;
  prevChainHash: string | null;
  locationId: string | null;
};

type DeviceRow = {
  tenantId: string;
  tenantName: string | null;
  deviceId: string;
  calls: number;
  failed: number;
  firstAt: string;
  lastAt: string;
  maxSeq: number;
};

type Verdict = {
  ok: boolean;
  checked: number;
  deviceId: string;
  tenantId: string;
  tenantName: string | null;
  failedAtSeq?: number;
  reason?: string;
};

const PAGE_SIZE = 50;

export function FiscalCallsPage() {
  const [rows, setRows] = useState<CallRow[]>([]);
  const [total, setTotal] = useState(0);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [verdicts, setVerdicts] = useState<Verdict[] | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [deviceId, setDeviceId] = useState('');
  const [status, setStatus] = useState<'all' | 'ok' | 'failed'>('all');
  const [days, setDays] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams();
      if (deviceId) q.set('deviceId', deviceId);
      if (status !== 'all') q.set('status', status);
      if (days) q.set('days', days);
      if (search.trim()) q.set('search', search.trim());
      q.set('limit', String(PAGE_SIZE));
      q.set('offset', String(offset));

      const [lista, ured] = await Promise.all([
        api.get<{ calls: CallRow[]; total: number }>(`/admin/fiscal-calls?${q}`),
        api.get<{ devices: DeviceRow[] }>('/admin/fiscal-calls/devices'),
      ]);
      setRows(lista.calls);
      setTotal(lista.total);
      setDevices(ured.devices);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [deviceId, status, days, search, offset]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { setOffset(0); }, [deviceId, status, days, search]);

  const proveri = async () => {
    setVerifying(true); setError(null);
    try {
      const q = deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : '';
      const res = await api.get<{ ok: boolean; devices: Verdict[] }>(`/admin/fiscal-calls/verify${q}`);
      setVerdicts(res.devices);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Provera nije uspela');
    } finally {
      setVerifying(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const pokvareni = verdicts?.filter((v) => !v.ok) ?? [];

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            Zabeleženi pozivi ka PFR-u
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Šta je poslato i šta je vraćeno, sa lancem otisaka - za veštačenje
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={proveri} disabled={verifying} style={btnPrimary} data-testid="calls-verify">
          {verifying ? <RefreshCw size={13} className="spin" /> : <ShieldCheck size={13} />}
          {deviceId ? 'Proveri lanac uređaja' : 'Proveri sve lance'}
        </button>
        <button onClick={refresh} disabled={loading} style={btnSecondary}>
          <RefreshCw size={13} /> Osveži
        </button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      {/* Nalaz provere - najvažnija stvar na ekranu, pa stoji iznad svega. */}
      {verdicts && (
        <div
          data-testid="calls-verdict"
          style={{
            ...kutija,
            marginBottom: 14,
            borderColor: pokvareni.length ? '#ef4444' : '#22c55e',
            background: pokvareni.length ? 'rgba(239,68,68,0.10)' : 'rgba(34,197,94,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: pokvareni.length ? '#fca5a5' : '#86efac' }}>
            {pokvareni.length ? <ShieldAlert size={16} /> : <ShieldCheck size={16} />}
            {pokvareni.length
              ? `Lanac je narušen na ${pokvareni.length} od ${verdicts.length} uređaja`
              : `Lanac je netaknut - provereno ${verdicts.length} uređaja, ${verdicts.reduce((s, v) => s + v.checked, 0)} zapisa`}
          </div>
          {pokvareni.map((v) => (
            <div key={`${v.tenantId ?? ''}:${v.deviceId}`} style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text)' }}>
              <span style={mono}>{v.deviceId.slice(0, 12)}…</span>
              {v.tenantName ? ` · ${v.tenantName}` : ''} · redni broj <strong>{v.failedAtSeq}</strong> - {v.reason}
            </div>
          ))}
          {/* Bez ovoga „netaknuto" izgleda jače nego što jeste. */}
          {!pokvareni.length && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
              Provera pokriva ono što je zabeleženo. Poziv koji nikad nije stigao sa uređaja vidi se kao rupa u rednim brojevima, ne kao narušen lanac.
            </div>
          )}
        </div>
      )}

      <div style={{ ...kutija, marginBottom: 14, display: 'grid', gridTemplateColumns: '2fr repeat(2, 1fr) 2fr auto', gap: 10, alignItems: 'end' }}>
        <Field label="Uređaj">
          <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)} style={input} data-testid="calls-device">
            <option value="">Svi uređaji</option>
            {devices.map((d) => (
              <option key={`${d.tenantId ?? ''}:${d.deviceId}`} value={d.deviceId}>
                {(d.tenantName ?? 'nepoznat klijent')} · {d.deviceId.slice(0, 8)}… ({d.calls})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ishod">
          <select value={status} onChange={(e) => setStatus(e.target.value as any)} style={input}>
            <option value="all">Sve</option>
            <option value="ok">Prošlo</option>
            <option value="failed">Palo ili prekinuto</option>
          </select>
        </Field>
        <Field label="Period">
          <select value={days} onChange={(e) => setDays(e.target.value)} style={input}>
            <option value="">Sve vreme</option>
            <option value="1">Poslednja 24h</option>
            <option value="7">Poslednjih 7 dana</option>
            <option value="30">Poslednjih 30 dana</option>
          </select>
        </Field>
        <Field label="Pretraga">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="calls-search"
            placeholder="PFR broj sa isečka, npr. 49JR4QUC-…-52841"
            title="Traži se po PFR broju sa isečka, po našem broju računa (2026-0001), po oznaci zahteva i po uređaju."
            style={input}
          />
        </Field>
        <button
          onClick={() => { setDeviceId(''); setStatus('all'); setDays(''); setSearch(''); setVerdicts(null); }}
          style={{ ...btnSecondary, alignSelf: 'end' }}
        >
          Resetuj
        </button>
      </div>

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          {/* `fixed` + sirine: bez toga duga poruka greske razvuce tabelu i
              istera desne kolone van vidnog polja. */}
          <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', tableLayout: 'fixed' }} data-testid="calls-table">
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={{ ...th, width: 150 }}>Kada</th>
                <th style={{ ...th, width: 56, textAlign: 'right' }}>Redni</th>
                <th style={{ ...th, width: 86 }}>Kanal</th>
                <th style={{ ...th, width: 96 }}>Posao</th>
                <th style={{ ...th, width: 150 }}>Klijent</th>
                <th style={{ ...th, width: 230 }}>Broj računa</th>
                <th style={th}>Ishod</th>
                <th style={{ ...th, width: 74, textAlign: 'right' }}>Trajanje</th>
                <th style={{ ...th, width: 50, textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  Nema zabeleženih poziva za izabrane filtere.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDate(r.occurredAt)}</td>
                  <td style={{ ...td, textAlign: 'right', ...mono, color: 'var(--color-text-muted)' }} title={`Redni broj poziva na uređaju ${r.deviceId.slice(0, 8)}…`}>{r.deviceSeq}</td>
                  <td style={td}><ProviderBadge provider={r.provider} /></td>
                  {/* Šta je poziv radio - bez toga red „invoice" i red „status" izgledaju isto. */}
                  <td style={{ ...td, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opisPosla(r.operation)}</td>
                  <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.tenantName ?? '-'}</td>
                  {/*
                    PFR broj gore, naš ispod.

                    Ranije je ovde stajao samo naš broj (`2026-0001`) - isti u
                    svakom redu, jer se vrti po lokaciji i godini. Ono što račun
                    zaista razlikuje, i jedino što čovek ima na papiru, je PFR
                    broj.
                  */}
                  <td style={{ ...td, overflow: 'hidden' }}>
                    <div style={{ ...mono, fontSize: 12, color: 'var(--color-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.pfrNumber ?? '-'}
                    </div>
                    <div style={{ ...mono, fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {r.entityId ?? '-'}
                    </div>
                  </td>
                  <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <IshodBadge row={r} />
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {r.durationMs != null ? `${r.durationMs} ms` : '-'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setSelectedId(r.id)} style={iconBtn} title="Pun zapis" data-testid="calls-open">
                      <Eye size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {total} zapisa · strana {currentPage} od {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0} style={iconBtn}><ChevronLeft size={16} /></button>
            <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={currentPage >= totalPages} style={iconBtn}><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {selectedId && <CallDetailDialog id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

/**
 * Pun zapis. Tela se povlače tek ovde - u spisku bi vukla megabajte, a čitaju
 * se tek kad se konkretan račun ispituje.
 */
function CallDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [row, setRow] = useState<CallDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose);

  useEffect(() => {
    api.get<CallDetail>(`/admin/fiscal-calls/${id}`)
      .then(setRow)
      .catch((e: any) => setError(e?.data?.message || 'Zapis nije učitan'));
  }, [id]);

  return (
    <div style={overlay}>
      <div style={{ ...dialog, maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={dialogHeader}>
          <strong style={{ fontSize: 15, color: 'var(--color-text-strong)' }}>
            Zapis poziva{row?.entityId ? ` - ${row.entityId}` : ''}
          </strong>
          <button onClick={onClose} style={closeBtn}><X size={18} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto' }}>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          {!row && !error && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Učitavanje…</div>}

          {row && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
                <DetailRow label="Klijent" value={row.tenantName ?? row.tenantId} />
                <DetailRow label="Uređaj" value={row.deviceId} mono />
                <DetailRow label="Redni broj" value={String(row.deviceSeq)} mono />
                <DetailRow label="Kanal" value={row.provider.toUpperCase()} />
                <DetailRow label="Kada se desio" value={formatDate(row.occurredAt)} />
                <DetailRow label="Kada je zabeležen" value={formatDate(row.recordedAt)} />
                <DetailRow label="Odgovor" value={row.responseCode != null ? String(row.responseCode) : 'bez odgovora (prekid)'} />
                <DetailRow label="Trajanje" value={row.durationMs != null ? `${row.durationMs} ms` : '-'} />
                <DetailRow label="Oznaka zahteva" value={row.requestId ?? '-'} mono />
              </div>

              {row.errorMessage && (
                <div style={{ ...errorBox, display: 'block', marginBottom: 18 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Greška</div>
                  <div style={{ fontSize: 12, wordBreak: 'break-word' }}>{row.errorMessage}</div>
                </div>
              )}

              {/* Otisci - ono što zapis čini dokazom. */}
              <div style={{ ...kutija, marginBottom: 18, background: 'var(--color-bg)' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 8 }}>
                  Lanac
                </div>
                <HashRow label="Otisak sadržaja" value={row.payloadHash} />
                <HashRow label="Prethodna karika" value={row.prevChainHash ?? '- (prvi u lancu)'} />
                <HashRow label="Karika ovog zapisa" value={row.chainHash} />
              </div>

              <BodyBlock title={`Zahtev - ${row.httpMethod} ${row.url}`} value={row.requestBody} />
              <BodyBlock title="Odgovor" value={row.responseBody} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BodyBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 6, wordBreak: 'break-all' }}>
        {title}
      </div>
      <pre style={{
        margin: 0, padding: 12, borderRadius: 8, maxHeight: 320, overflow: 'auto',
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        fontSize: 11, lineHeight: 1.5, color: 'var(--color-text)',
        fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {value == null ? '-' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function HashRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--color-text-muted)', minWidth: 150 }}>{label}</span>
      <span style={{ ...mono, fontSize: 11, color: 'var(--color-text)', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

/** VPFR ide preko naseg servera, LPFR direktno sa uredjaja - bitna razlika. */
function ProviderBadge({ provider }: { provider: CallRow['provider'] }) {
  const map = {
    vpfr: { Icon: Cloud, label: 'VPFR', bg: 'rgba(59,130,246,0.15)', color: '#93c5fd' },
    lpfr: { Icon: HardDrive, label: 'LPFR', bg: 'rgba(124,58,237,0.15)', color: '#c4b5fd' },
    mock: { Icon: Cpu, label: 'MOCK', bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' },
  }[provider];
  const { Icon, label, bg, color } = map;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 600, background: bg, color }}>
      <Icon size={11} /> {label}
    </span>
  );
}

/**
 * Prekid veze NIJE isto što i odbijen zahtev: kod prekida se ne zna da li je
 * račun izdat, pa se prikazuje posebno.
 */
function IshodBadge({ row }: { row: CallRow }) {
  if (row.responseCode == null) {
    return <span style={badge('rgba(245,158,11,0.15)', '#fcd34d')} title={row.errorMessage ?? undefined}>Bez odgovora · {skrati(row.errorMessage)}</span>;
  }
  const ok = row.responseCode >= 200 && row.responseCode < 300;
  /*
    Golo „200" nije ishod nego HTTP status. Ovaj ekran čitaju podrška i veštak,
    pa piše šta se desilo, a broj ostaje uz reč - za onoga ko ga traži.
  */
  return (
    <span
      style={badge(ok ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', ok ? '#86efac' : '#fca5a5')}
      title={row.errorMessage ?? undefined}
    >
      {ok ? `Prošlo · ${row.responseCode}` : `Odbio PFR · ${row.responseCode} · ${skrati(row.errorMessage)}`}
    </span>
  );
}

/** Šta je poziv radio - `invoice` i `status` su naši ključevi, ne reč za čoveka. */
function opisPosla(operation: string): string {
  const map: Record<string, string> = {
    invoice: 'Izdavanje',
    status: 'Provera veze',
    verify: 'Provera',
    attention: 'Poruka PFR-a',
  };
  return map[operation] ?? operation;
}

const skrati = (s: string | null) => (s ? (s.length > 60 ? `${s.slice(0, 60)}…` : s) : 'prekid veze');

const badge = (bg: string, color: string): React.CSSProperties => ({
  fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 600, background: bg, color,
  display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
});

function DetailRow({ label, value, mono: isMono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2, fontWeight: 500 }}>{label}</label>
      <div style={{ fontSize: 13, color: 'var(--color-text)', fontFamily: isMono ? 'monospace' : undefined, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const formatDate = (iso: string) => new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

const mono: React.CSSProperties = { fontFamily: 'monospace' };
const kutija: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16 };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { ...btnSecondary, background: 'var(--color-primary)', color: '#fff', border: 'none' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', padding: 20 };
const dialog: React.CSSProperties = { width: '100%', background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, outline: 'none' };
